/** Referencia macro Colombia — punto de partida para proyección (indicativos). */
export const MESES_HORIZONTE = 36;

export const MACRO_PUNTO_INICIO = {
  etiqueta: "Ene 2025 (DANE / BanRep)",
  inflacionAnual: 5.2,
  actividadPrivada: 102.4,
  tasaInteres: 9.25,
};

const percentil = (valores, p) => {
  if (!valores.length) return 0;
  const sorted = [...valores].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor((p / 100) * (sorted.length - 1))));
  return sorted[idx];
};

/** Etiqueta de eje: solo inicios de trimestre (Año 1 · T1 … Año 3 · T4). */
export function etiquetaTrimestreEje(index) {
  const monthInYear = (index % 12) + 1;
  if (monthInYear !== 1 && monthInYear !== 4 && monthInYear !== 7 && monthInYear !== 10) {
    return "";
  }
  const year = Math.floor(index / 12) + 1;
  const quarter = Math.ceil(monthInYear / 3);
  return `Año ${year} · T${quarter}`;
}

/** Índices 0-based de cada inicio de trimestre en el horizonte de 36 meses. */
export const TICKS_TRIMESTRE = Array.from({ length: 12 }, (_, q) => q * 3);

export function etiquetaMesHorizonte(index) {
  return etiquetaTrimestreEje(index) || `Mes ${index + 1}`;
}

/** Convierte mes de simulación (1–36) al año del horizonte (1, 2 o 3). */
export function etiquetaAnioImprevisto(mes) {
  const year = Math.min(3, Math.max(1, Math.ceil(mes / 12)));
  return `Año ${year}`;
}

/** Periodo legible para tooltips (siempre año + trimestre). */
export function etiquetaPeriodoHorizonte(index) {
  const year = Math.floor(index / 12) + 1;
  const monthInYear = (index % 12) + 1;
  const quarter = Math.ceil(monthInYear / 3);
  return `Año ${year} · T${quarter}`;
}

function gaussianRandom() {
  const u = Math.random() || 1e-9;
  const v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/** Trayectoria central determinística del IPC. */
function medianaInflacion(i, meses) {
  const { inflacionAnual } = MACRO_PUNTO_INICIO;
  const t = meses > 1 ? i / (meses - 1) : 0;
  return inflacionAnual - t * 1.05 + Math.sin(i * 0.42) * 0.14;
}

/**
 * σ(t) crece con el horizonte → abanico tipo campana que se ensancha hacia adelante.
 * En t=0 todas las trayectorias parten del mismo punto (incertidumbre nula).
 */
function sigmaInflacion(i, meses) {
  const t = meses > 1 ? i / (meses - 1) : 0;
  if (i === 0) return 0;
  const sigmaMax = 1.85;
  return sigmaMax * Math.pow(t, 0.58);
}

function trayectoriaInflacionAleatoria(meses) {
  const z = gaussianRandom();
  let rw = 0;
  const volMensual = 0.055;

  return Array.from({ length: meses }, (_, i) => {
    const central = medianaInflacion(i, meses);
    const sigma = sigmaInflacion(i, meses);

    if (i > 0) {
      rw += gaussianRandom() * volMensual;
    }

    const t = meses > 1 ? i / (meses - 1) : 0;
    const componenteCampana = sigma * z;
    const componenteCaminata = rw * Math.sqrt(t) * 0.45;

    const valor = central + componenteCampana + componenteCaminata;
    return Number(Math.max(1.2, Math.min(16, valor)).toFixed(2));
  });
}

/** Monte Carlo de inflación con bandas por percentiles (campana que se abre en el tiempo). */
export function proyectarInflacionConBandas(meses = MESES_HORIZONTE, iteraciones = 220) {
  const paths = Array.from({ length: iteraciones }, () => trayectoriaInflacionAleatoria(meses));

  const filas = Array.from({ length: meses }, (_, i) => {
    const vals = paths.map((p) => p[i]);
    const p5 = percentil(vals, 5);
    const p10 = percentil(vals, 10);
    const p25 = percentil(vals, 25);
    const p50 = percentil(vals, 50);
    const p75 = percentil(vals, 75);
    const p90 = percentil(vals, 90);
    const p95 = percentil(vals, 95);

    return {
      idx: i,
      mes: etiquetaPeriodoHorizonte(i),
      inflacion: p50,
      p5,
      p10,
      p25,
      p50,
      p75,
      p90,
      p95,
      amplitudBanda: Number((p95 - p5).toFixed(3)),
      stackBase: p5,
      bandP5P10: Number((p10 - p5).toFixed(3)),
      bandP10P25: Number((p25 - p10).toFixed(3)),
      bandP25P50: Number((p50 - p25).toFixed(3)),
      bandP50P75: Number((p75 - p50).toFixed(3)),
      bandP75P90: Number((p90 - p75).toFixed(3)),
      bandP90P95: Number((p95 - p90).toFixed(3)),
    };
  });

  // Garantizar abanico monótono: la incertidumbre solo crece con el horizonte
  let anchoMinimo = 0;
  return filas.map((fila) => {
    const ancho = Math.max(fila.amplitudBanda, anchoMinimo);
    anchoMinimo = ancho;

    if (Math.abs(ancho - fila.amplitudBanda) < 0.008) return fila;

    const centro = fila.p50;
    const half = ancho / 2;
    const p5 = Number((centro - half).toFixed(2));
    const p95 = Number((centro + half).toFixed(2));
    const span = p95 - p5;

    return {
      ...fila,
      p5,
      p10: Number((centro - span * 0.36).toFixed(2)),
      p25: Number((centro - span * 0.2).toFixed(2)),
      p75: Number((centro + span * 0.2).toFixed(2)),
      p90: Number((centro + span * 0.36).toFixed(2)),
      p95,
      amplitudBanda: span,
      stackBase: p5,
      bandP5P10: Number((centro - span * 0.36 - p5).toFixed(3)),
      bandP10P25: Number((centro - span * 0.2 - (centro - span * 0.36)).toFixed(3)),
      bandP25P50: Number((centro - (centro - span * 0.2)).toFixed(3)),
      bandP50P75: Number((centro + span * 0.2 - centro).toFixed(3)),
      bandP75P90: Number((centro + span * 0.36 - (centro + span * 0.2)).toFixed(3)),
      bandP90P95: Number((p95 - (centro + span * 0.36)).toFixed(3)),
    };
  });
}

/** Proyección macro mensual anclada al punto inicial (actividad y tasas). */
export function proyectarMacroColombia(meses = MESES_HORIZONTE) {
  const { actividadPrivada, tasaInteres } = MACRO_PUNTO_INICIO;
  const inflacionBandas = proyectarInflacionConBandas(meses);

  return Array.from({ length: meses }, (_, i) => {
    const t = meses > 1 ? i / (meses - 1) : 0;
    const estacional = Math.sin(i * 0.35) * 1.6;
    const ciclo = Math.cos(i * 0.22) * 2.1;

    return {
      ...inflacionBandas[i],
      actividadPrivada: Number((actividadPrivada - t * 4.2 + estacional + ciclo * 0.4).toFixed(1)),
      tasaInteres: Number((tasaInteres - t * 1.4 + Math.sin(i * 0.28) * 0.35).toFixed(2)),
    };
  });
}
